import React from 'react';
import PropTypes from 'prop-types';
import cx from 'classnames';
import translate from '../../../commonComponents/translate';
import ModuleProp from '../propTypes/moduleProp';
import {
  displayTrophyName,
  LESSON_STATUS,
  LESSON_TYPE,
  MODULE_STATUS,
  MODULE_TYPE,
} from '../../../services/utilityFunctions';

function ModuleList(props) {
  const {
    t,
    modules,
    onLessonSelected,
    selectedLesson,
    onModuleSelected,
    selectedModule,
  } = props;
  const handleModuleToggle = id =>
    id === selectedModule ? onModuleSelected(null) : onModuleSelected(id);

  const moduleClass = mod =>
    cx('course-module-list-item', {
      selected: mod._id === selectedModule,
      trophy: mod.type === MODULE_TYPE.TROPHY,
      'general-info': mod.type === MODULE_TYPE.INFO,
      locked: mod.status === MODULE_STATUS.LOCKED,
    });

  const lessonClass = lesson =>
    cx('course-lesson-selector', {
      selected: lesson._id === selectedLesson,
      locked: lesson.status === LESSON_STATUS.LOCKED,
    });

  const statusInfo = course => {
    switch (course.status) {
      case MODULE_STATUS.DONE:
        return { color: '#7ed322', src: '/images/module/done.svg' };
      case MODULE_STATUS.UNLOCKED:
        return { color: '#7ed322', src: '/images/module/unlocked.svg' };
      case MODULE_STATUS.LOCKED:
        return { color: '#ffb822', src: '/images/module/locked.svg' };
      case MODULE_STATUS.IN_PROGRESS:
        return { color: '#bee990', src: '/images/module/marker.svg' };
      default:
        return null;
    }
  };

  const moduleImage = mod => {
    if (mod.type === MODULE_TYPE.TROPHY) {
      return `/images/trophies/trophy_level_${mod.level}.svg`;
    }
    if (mod.type === MODULE_TYPE.INFO) {
      return '/images/academy/info.svg';
    }
    if (mod.type === MODULE_TYPE.LESSONS) {
      return mod?.image?.url || '';
    }
    if (statusInfo(mod)) {
      return statusInfo(mod).src;
    }
    return null;
  };

  return (
    <div className="m-portlet m-portlet--rounded course-module-list-container">
      <div className="m-portlet__head">
        <div className="m-portlet__head-caption">
          <div className="m-portlet__head-title">
            <h3 className="m-portlet__head-text">
              {t('academyDetails_modules')}
            </h3>
          </div>
        </div>
      </div>
      <div className="m-portlet__body course-module-list">
        {modules.map(mod => (
          <div key={mod._id} className={moduleClass(mod)}>
            <div
              className="course-module-status"
              style={
                statusInfo(mod)
                  ? { backgroundColor: statusInfo(mod).color }
                  : {}
              }
            >
              {moduleImage(mod) && (
                <img alt="module status" src={moduleImage(mod)} />
              )}
            </div>
            <div
              className="course-module-status-right"
              style={
                statusInfo(mod)
                  ? { backgroundColor: statusInfo(mod).color }
                  : {}
              }
            >
              {statusInfo(mod) && (
                <img alt="module status" src={statusInfo(mod).src} />
              )}
            </div>
            <button
              type="button"
              className="course-module-selector"
              onClick={() => handleModuleToggle(mod._id)}
            >
              <div className="details-text">
                <div className="details-name">
                  {mod.type === MODULE_TYPE.TROPHY
                    ? displayTrophyName(t, mod)
                    : mod.name}
                </div>
                <div className="details-description">
                  <span>
                    {mod.type === MODULE_TYPE.TROPHY
                      ? t('academyDetails_trophy')
                      : mod.description}
                  </span>
                </div>
              </div>
            </button>
            <div className="course-module-lesson-list">
              {mod?.lessonsList?.map(lesson => {
                const isQuiz = lesson.type === LESSON_TYPE.QUIZ;
                const lessonName =
                  lesson.name ||
                  `${t('academyDetails_lesson')} ${lesson.pos + 1}`;
                const lessonDescription =
                  lesson.description ||
                  t(`academyDetails_lessonType_${lesson.type}`);

                return (
                  <button
                    type="button"
                    key={lesson._id}
                    className={lessonClass(lesson)}
                    onClick={() => onLessonSelected(lesson._id)}
                  >
                    <div
                      className="course-lesson-status"
                      style={
                        statusInfo(lesson)
                          ? { backgroundColor: statusInfo(lesson).color }
                          : {}
                      }
                    >
                      {statusInfo(lesson) && (
                        <img alt="module status" src={statusInfo(lesson).src} />
                      )}
                    </div>
                    <div className="details-text">
                      <div className="details-name">
                        {isQuiz
                          ? t('academyDetails_lessonType_quiz')
                          : lessonName}
                      </div>
                      <div className="details-type">
                        {isQuiz
                          ? t(`academyDetails_lessonType_${lesson.type}`)
                          : lessonDescription}
                      </div>
                    </div>
                    {lesson.contentType && (
                      <div className="course-lesson-contentType-container">
                        <div className="course-lesson-contentType">
                          <img
                            alt={t(
                              `academyDetails_contentType_${
                                lesson.contentType
                              }`,
                            )}
                            src={`/images/academy/contentType/${
                              lesson.contentType
                            }.svg`}
                          />
                        </div>
                      </div>
                    )}
                    {isQuiz && (
                      <div className="course-lesson-contentType-container">
                        <div className="course-lesson-contentType">
                          <img
                            alt={t('academyDetails_lessonType_quiz')}
                            src="/images/academy/contentType/quiz.svg"
                          />
                        </div>
                      </div>
                    )}
                  </button>
                );
              })}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

ModuleList.propTypes = {
  t: PropTypes.func.isRequired,
  modules: PropTypes.arrayOf(PropTypes.shape(ModuleProp.propType)).isRequired,
  selectedLesson: PropTypes.string,
  onLessonSelected: PropTypes.func.isRequired,
  selectedModule: PropTypes.string,
  onModuleSelected: PropTypes.func.isRequired,
};

ModuleList.defaultProps = {
  selectedLesson: null,
  selectedModule: null,
};

export default translate(ModuleList);
